<template>
    <div class="left">
        <div class="container scrollable-content" >
            <div class="d-flex justify-content-around flex-wrap">
                <template-editor-button icon="icon-cph_iconset_invoice"
                                        class="m-0"
                                        :buttonTitle="$t('LAYOUTEDITOR.elements')"
                                        :flex="false"
                                        :active="activeTab === 'elements'"
                                        @click="activeTab = 'elements'"/>
                <template-editor-button icon="icon-cph_iconset-Kopie_cog"
                                        class="m-0"
                                        :buttonTitle="$t('LAYOUTEDITOR.actions')"
                                        :flex="false"
                                        :active="activeTab === 'actions'"
                                        @click="activeTab = 'actions'"/>
                <template-editor-button icon="icon-cph_iconset_fotos"
                                        class="m-0"
                                        :buttonTitle="$t('LAYOUTEDITOR.previews')"
                                        :flex="false"
                                        :active="activeTab === 'previews'"
                                        @click="activeTab = 'previews'"/>
            </div>
            <template v-if="activeTab === 'actions'">
                <div class="buttons-container">
                    <h4>Actions:</h4>
                    <template-editor-button icon="icon-cph_iconset_burger_new"
                                            :buttonTitle="$t('LAYOUTEDITOR.templateGroups')"
                                            @click="$emit('openTemplateGroupManager')"/>
                    <template-editor-button icon="icon-cph_iconset_edit"
                                            :buttonTitle="$t('LAYOUTEDITOR.newLayout')"
                                            @click="selectedLayout.jsonData ? confirmAction({
                                                question: $t('LAYOUTEDITOR.clearLayoutConfirmation'),
                                                callback: clearLayout
                                            }) : clearLayout()"/>
                    <template-editor-button icon="icon-cph_iconset_trash_can"
                                            :buttonTitle="$t('LAYOUTEDITOR.deleteLayout')"
                                            :disabled="!layoutSelected || newLayout"
                                            @click="confirmAction({
                                                question: $t('LAYOUTEDITOR.deleteLayoutConfirmation'),
                                                callback: deleteLayout
                                             })"/>
                    <template-editor-button icon="icon-cph_iconset_dropbox"
                                            :buttonTitle="$t('LAYOUTEDITOR.deleteAllLayout')"
                                            @click="confirmAction({
                                                question: $t('LAYOUTEDITOR.deleteAllLayoutsConfirmation'),
                                                callback: deleteAllLayouts
                                            })"/>
                    <template-editor-button icon="icon-cph_iconset_search"
                                            :buttonTitle="$t('UI.open')"
                                            @click="openModal(() => {}, {}, $t('UI.open'), true)"/>
                    <template-editor-button icon="icon-cph_iconset_tick"
                                            :buttonTitle="$t('UI.save')"
                                            :disabled="!layoutSelected || newLayout"
                                            @click="openModal(saveLayout, {}, $t('UI.save'))"/>
                    <template-editor-button icon="icon-cph_iconset_plus"
                                            :buttonTitle="$t('UI.saveAsNew')"
                                            :disabled="!layoutSelected"
                                            @click="openModal(addLayout, {}, $t('UI.saveAsNew'))"/>
                    
                    <template-editor-button icon="icon-cph_iconset_invoice"
                                            :buttonTitle="$t('LAYOUTEDITOR.exportLayout')"
                                            :disabled="!layoutSelected"
                                            @click="exportLayout">
                        <a id="downloadAnchorElem" style="display:none"></a>
                    </template-editor-button>
                    
                    <template-editor-button icon="icon-cph_iconset_downloads"
                                            :buttonTitle="$t('LAYOUTEDITOR.exportAll')"
                                            @click="exportAllLayouts">
                        <a id="downloadAnchorElements" style="display:none"></a>
                    </template-editor-button>
                    
                    <template-editor-button icon="icon-cph_iconset_medium"
                                            :buttonTitle="$t('LAYOUTEDITOR.import')"
                                            @click="importLayouts"/>
                    
                    <template-editor-button icon="icon-cph_iconset_rotate"
                                            :buttonTitle="$t('LAYOUTEDITOR.rotate')"
                                            :disabled="!layoutSelected"
                                            @click="rotateLayout"/>
                </div>
            </template>
            <template v-else-if="activeTab === 'previews'">
                <div class="row" v-if="layoutSelected">
                    <div class="col-12">
                        <h3>Previews:</h3>
                        <div v-for="format in formats" :key="format.id" class="layout-preview"
                             v-if="currentLayout.attrs.style">
                            <span>{{format.id}}</span>
                            <div :style="{
                                    transform: `scale(${calculateScale(format, 150)})`,
                                    'transform-origin': 'top left'
                                }">
                                <rendered-template :eventListeners="false"
                                                   :preview="true"
                                                   :layout="getFormattedLayout(format)"
                                                   :style="{border: '5px solid white'}"/>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template v-else-if="activeTab === 'elements'">
                <h4>{{$t('LAYOUTEDITOR.availableElements')}}</h4>
                <div class="elements-container">
                    <elements-category class="categories"/>
                </div>
                <layout-selection :selectedLayout.sync="selectedLayout"
                                  :options="layouts"
                                  @selected="fetchLayout($event)"></layout-selection>
                
                <sibling-selection :options="possibleSiblings"
                                   :layoutRotatedSibling.sync="layoutRotatedSibling"
                                   @removed="layoutRotatedSibling = {}"
                                   @selected="layoutRotatedSibling = $event"
                                   v-if="layoutSelected"/>
    
                <div class="form-group">
                    <label class="form-label">Rotatable: </label>
                    <input type="checkbox" v-model="layoutRotatable">
                </div>
                
                <format-selection :options="formats"
                                  :selectedFormat="selectedFormat"
                                  @selected="updateLayoutView"
                                  v-if="layoutSelected"/>
                
                <available-formats-selection :options="formats"
                                             :key="selectedLayout.id"
                                             :availableFormatsList.sync="availableFormatsList"
                                             v-if="layoutSelected"/>
            
            </template>
        </div>
        <transition name="fade" mode="out-in">
            <functional-modal @close="modal.open = false"
                              key="layout-modal"
                              @save="modal.callback(modal.data); modal = false"
                              :showModal="modal.open"
                              :showSave="!openingLayout"
                              :title="modal.title ? modal.title : $t('UI.edit')"
                              v-if="modal.open">
                <div :class="['col-12']" slot="body" v-if="!openingLayout">
                    <div class="form-group">
                        <label for="filename">
                            {{$t('LAYOUTEDITOR.filename')}}:
                        </label>
                        <input type="text"
                               class="form-control"
                               id="filename"
                               v-model="selectedLayout.filename">
                    </div>
                    <div class="form-group">
                        <label for="description">
                            {{$t('LAYOUTEDITOR.description')}}:
                        </label>
                        <input type="text"
                               class="form-control"
                               id="description"
                               v-model="selectedLayout.description">
                    </div>
                    <div class="form-group">
                        <label for="title">{{$t('LAYOUTEDITOR.title')}}::</label>
                        <input type="text"
                               class="form-control"
                               id="title"
                               v-model="selectedLayout.title">
                    </div>
                </div>
                <div :class="['col-12']" slot="body" v-else-if="openingLayout">
                    <layout-selection :selectedLayout.sync="selectedLayout"
                                      :options="layouts"
                                      @selected="fetchLayout($event)"/>
                </div>
            </functional-modal>
        </transition>
        <input type="file" ref="fileInput" class="hidden-input">
    </div>
</template>

<script type="text/babel">
import {mapActions, mapGetters, mapMutations} from 'vuex';
import ElementsCategory from '../../Available/Category/Elements/ElementsCategory.vue';
import LayersMixin from '../../../../../../mixins/LayersMixin.vue';
import DragAndDropMixin from '../../../../../../mixins/DragAndDropMixin.vue';
import RenderedTemplate from '../../../Rendered/RenderedTemplate.vue';
import FunctionalModal from '../../../../../Shared/Modal/FunctionalModal';
import ImportMixin from '../../../../../../mixins/importMixin.vue';
import Icon from '../../../../../Shared/Icons/Icon.vue';
import Multiselect from 'vue-multiselect';
import TemplateEditorButton from './TemplateEditorButton';
import LayoutSelection from './LayoutSelection';
import FormatSelection from './FormatSelection';
import SiblingSelection from './SiblingSelection';
import AvailableFormatsSelection from './AvailableFormatsSelection';

export default {
    name: 'WorkspaceAreaLeft',
    data () {
        return {
            newLayout: false,
            activeTab: 'actions',
            importedLayout: '',
            selectedLayout: {},
            layoutSelected: false,
            openingLayout: false,
            selectedFormat: {},
            displayRotatableSibling: false,
            rotate: false,
            overwrite: false,
            modal: {
                open: false,
                callback: () => {},
                data: {}
            }
        };
    },
    components: {
        TemplateEditorButton,
        Icon,
        RenderedTemplate,
        FunctionalModal,
        Multiselect,
        LayoutSelection,
        FormatSelection,
        SiblingSelection,
        AvailableFormatsSelection,
        ElementsCategory
    },
    computed: {
        ...mapGetters({
            layouts: 'getLayouts',
            formats: 'getFormats',
            currentLayout: 'getCurrentLayout',
            currentScale: 'getScale',
            parsedScale: 'getParsedScale'
        }),
        availableFormatsList: {
            get () {
                return this.currentLayout.availableFormats || [];
            },
            set (val) {
                this.$vuexSet('currentLayout.availableFormats', val);
            }
        },
        possibleSiblings () {
            return this.layouts.map(layout => ({id: layout.id, title: layout.title}));
        },
        layoutRotatable: {
            get () {
                if(!this.currentLayout || !this.currentLayout.metadata) {
                    return true;
                } else if (this.currentLayout.metadata.layoutRotatedSibling
                    && this.currentLayout.metadata.layoutRotatedSibling.id) {
                    return false;
                } else if(this.currentLayout.metadata.rotatable === undefined) {
                    return true;
                }
                return this.currentLayout.metadata.rotatable;
            },
            set (val) {
                this.updateMetadata({...this.currentLayout.metadata, rotatable: val});
            }
        },
        layoutRotatedSibling: {
            get () {
                if(!this.currentLayout || !this.currentLayout.metadata) {
                    return '';
                }
                return this.currentLayout.metadata.layoutRotatedSibling;
            },
            set (val) {
                if(!this.currentLayout.metadata) {
                    this.currentLayout.metadata = {};
                }
                
                if((val && !val.id)) {
                    this.updateMetadata({
                        ...this.currentLayout.metadata,
                        layoutRotatedSibling: {},
                        rotatable: true
                    });
                } else {
                    this.updateMetadata({
                        ...this.currentLayout.metadata,
                        layoutRotatedSibling: val,
                        rotatable: false
                    });
                }
            }
        },
        getLayoutIds () {
            return this.layouts.map(layout => layout.id);
        }
    },
    mixins: [LayersMixin, DragAndDropMixin, ImportMixin],
    methods: {
        ...mapActions([
            'getLayoutAsync',
            'putLayoutAsync',
            'updateLayoutAsync',
            'changeLayoutFormat',
            'putLayoutAsync',
            'deleteLayoutAsync',
            'addLayoutAsync',
            'confirmAction',
            'fetchLayoutsAsync',
            'clearEditedElement'
        ]),
        ...mapMutations([
            'resetLayout',
            'addAlert',
            'updateEditedElement',
            'updateEditedElementRootPath',
            'updateLoadingState',
            'updateMetadata',
            'updateScale'
        ]),
        rotateLayout () {
            this.$vuexSet('currentLayout.rotate', !this.currentLayout.rotate);
            this.updateLayoutView(this.selectedFormat);
        },
        openModal (callback, data, title, openingLayout) {
            this.openingLayout = openingLayout;
            this.modal = {
                open: true,
                callback,
                data,
                title
            };
        },
        exportLayout () {
            this.selectedLayout.jsonData = this.currentLayout;
            let dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(
                JSON.stringify({layouts: [this.selectedLayout]})
            );
            let downloadAnchorNode = document.createElement('a');
            downloadAnchorNode.setAttribute('href', dataStr);
            downloadAnchorNode.setAttribute('download', this.selectedLayout.title + '.json');
            downloadAnchorNode.click();
            downloadAnchorNode.remove();
        },
        exportAllLayouts () {
            this.$utils.exportData({data: {layouts: this.layouts}, fileName: 'layouts'});
        },
        clearLayout () {
            this.resetLayout();
            this.newLayout = true;
            this.selectedLayout = {
                description: '',
                jsonData: this.currentLayout,
                title: '',
                filename: '',
                sourceList: [],
                template: [],
                format: {
                    width: this.selectedFormat.width || 1920,
                    height: this.selectedFormat.height || 1240
                }
            };
            this.updateEditedElement({});
            this.updateEditedElementRootPath('');
            try {
                this.selectedFormat = this.formats[0];
                this.updateLayoutView(this.selectedFormat);
            } catch (e) {
                this.selectedFormat = {};
            }
            this.layoutSelected = true;
        },
        async refetch () {
            setTimeout(async () => {
                let {err} = await this.fetchLayoutsAsync();
                if(err) {
                    this.refetch();
                } else {
                    this.updateLoadingState(false);
                }
            }, 3000);
        },
        async importLayouts () {
            this.updateLoadingState(true);
            this.importLayoutList();
            this.refetch();
        },
        async addLayout () {
            let layoutToAdd = {
                ...this.selectedLayout,
                jsonData: this.currentLayout,
                format: this.selectedFormat
            };
            try {
                let { id } = await this.addLayoutAsync({layout: layoutToAdd});
                if(id) {
                    this.newLayout = false;
                    this.selectedLayout.id = id;
                    this.addAlert({
                        title: 'Successfully ',
                        type: 'success',
                        body: 'added layout'
                    });
                }
            } catch (err) {
                throw new Error(err);
            }
        },
        async saveLayout () {
            let layoutToAdd = {
                ...this.selectedLayout,
                jsonData: this.currentLayout,
                format: this.selectedFormat,
                jsonFilename: `${this.selectedLayout.title}.json`
            };
            await this.putLayoutAsync(layoutToAdd);
            this.addAlert({
                title: 'Successfully ',
                type: 'success',
                body: 'saved layout'
            });
        },
        async fetchLayout (layout) {
            this.layoutRotatedSibling = {};
            this.newLayout = false;
            try {
                let {data} = await this.getLayoutAsync({layout: layout});
                this.$vuexSet('currentLayout', data);
                try {
                    if(data.availableFormats.length > 0) {
                        this.selectedFormat = data.availableFormats[0];
                    } else {
                        this.selectedFormat = this.selectedFormat.id
                            ? this.selectedFormat
                            : this.formats[0];
                    }
                    this.selectedLayout = layout;
        
                } catch (e) {
                    this.selectedFormat = this.formats[0];
                }
            } catch (err) {
                throw new Error(err);
            }
            this.updateLayoutView(this.selectedFormat);
            this.updateEditedElement({});
            this.updateEditedElementRootPath('');
            this.layoutSelected = true;
        },
        async deleteLayout (id) {
            try {
                await this.deleteLayoutAsync({id: id || this.selectedLayout.id});
                this.addAlert({
                    title: 'Successfully ',
                    type: 'warning',
                    body: `deleted layout with id: ${this.selectedLayout.id}`
                });
                this.$vuexSet('currentLayout', {});
            } catch (err) {
                throw new Error(err);
            }
            this.selectedLayout = {};
            this.layoutSelected = false;
        },
        async deleteAllLayouts () {
            const promises = [];
            this.updateLoadingState(true);
            this.layouts.forEach(({id}) => {
                promises.push(this.deleteLayoutAsync({id, disableDispatch: true}));
            });
            try {
                await this.$utils.resolvePromises(promises);
                this.addAlert({
                    title: 'Successfully ',
                    type: 'warning',
                    body: `deleted ${promises.length} layouts`
                });
            } catch (err) {
                throw new Error(err);
            } finally {
                this.fetchLayoutsAsync();
            }
            this.updateLoadingState(false);
        },
        calculateScale (format, startWidth) {
            if(format.height > format.width) {
                let ar = format.height / format.width;
                startWidth /= ar;
            }
            return startWidth / format.width;
        },
        getFormattedLayout (format) {
            let newLayout = JSON.parse(JSON.stringify(this.currentLayout));
            newLayout.attrs.style.width = format.width + 'px';
            newLayout.attrs.style.height = format.height + 'px';
            return newLayout;
        },
        updateLayoutView (event) {
            let format = JSON.parse(JSON.stringify(event));
            if(this.currentLayout.rotate) {
                let temp = format.width;
                format.width = format.height;
                format.height = temp;
            }
            this.changeLayoutFormat(format);
            this.updateScale(this.calculateScale(format, 500));
            this.clearEditedElement();
        }
    },
    created () {
        this.clearLayout();
    }
};

</script>

<style lang="scss" scoped>
    .left {
        position: relative;
        @include color('background-color', primary);
        color: white;
        overflow: hidden;
        .scrollable-content {
            overflow-y: auto;
            padding-right: 15px;
            height: 100%;
            .buttons-container {
                display: flex;
                flex-direction: column;
                margin-bottom: 25px;
                .save-button {
                    background-color: #14ce1d;
                    
                }
                
                .add-button {
                    background-color: #040404;
                }
                
                .waste-bin {
                    background-color: red;
                    height: 60px;
                }
            }
            
            .layout-preview {
                overflow: hidden;
                max-height: 180px;
            }
            
            select.custom-select {
                height: 2em;
            }
        }
        
    }
</style>
